import React from 'react';
import { InsightsSectionProps } from '../../../types/health-tools';
import HealthCard from '../ui/HealthCard';
import ProgressBar from '../ui/ProgressBar';
import { SkeletonHealthCardsGrid } from '../ui/SkeletonHealthCard';
import { BarChart3, AlertTriangle, TrendingUp, Brain, Activity } from 'lucide-react';

/**
 * Health Insights Section Component
 * 
 * Displays health insights including health score, risk factors, and personalized health insights
 * 
 * @param healthScore - Health score data
 * @param riskFactors - Risk factors data
 * @param insights - Health insights data
 * @param isLoading - Whether data is currently loading
 * @param className - Additional CSS classes
 */
const InsightsSection: React.FC<InsightsSectionProps> = ({
  healthScore,
  riskFactors,
  insights,
  isLoading = false,
  className = ''
}) => {
  // If loading, show skeleton components
  if (isLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <SkeletonHealthCardsGrid count={3} columns={3} />
      </div>
    );
  }

  // Helper function to get the color for the health score
  const getHealthScoreColor = (score: number): string => {
    if (score >= 80) return 'var(--status-success)';
    if (score >= 60) return 'var(--brand-primary)';
    if (score >= 40) return 'var(--status-warning)';
    return 'var(--status-error)';
  };

  // Helper function to get the appropriate icon for insight type
  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'positive':
        return <TrendingUp className="h-4 w-4 text-[var(--status-success)]" />;
      case 'negative':
        return <AlertTriangle className="h-4 w-4 text-[var(--status-error)]" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-[var(--status-warning)]" />;
      case 'suggestion':
        return <Brain className="h-4 w-4 text-[var(--status-info)]" />;
      default:
        return <Brain className="h-4 w-4 text-[var(--status-info)]" />;
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Health Score Card */}
        <HealthCard 
          title="Health Score" 
          icon={Activity}
        >
          <div className="flex flex-col items-center py-4">
            <div className="mb-4 text-center">
              <div 
                className="relative inline-flex rounded-full justify-center items-center bg-[var(--background-surface)] h-32 w-32 mb-2"
              >
                <span className="text-4xl font-bold" style={{ color: getHealthScoreColor(healthScore.current) }}>
                  {healthScore.current}
                </span>
              </div>
              <p className="text-sm text-[var(--text-secondary)]">
                {healthScore.change.startsWith('+') ? (
                  <span className="text-[var(--status-success)]">{healthScore.change} points</span>
                ) : healthScore.change.startsWith('-') ? (
                  <span className="text-[var(--status-error)]">{healthScore.change} points</span>
                ) : (
                  <span>No change</span>
                )}
                {' '}since {healthScore.date}
              </p>
            </div>
            
            <div className="w-full space-y-3">
              {healthScore.breakdown.map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[var(--text-secondary)]">{item.category}</span>
                    <span className="text-[var(--text-primary)]">{item.score}/100</span>
                  </div>
                  <ProgressBar 
                    value={item.score} 
                    max={100} 
                    color={getHealthScoreColor(item.score)}
                    height={8}
                  />
                </div>
              ))}
            </div>
          </div>
        </HealthCard>

        {/* Risk Factors Card */}
        <HealthCard 
          title="Risk Factors" 
          icon={AlertTriangle}
        >
          <div className="space-y-4 py-2">
            {riskFactors.map((factor, index) => (
              <div key={index} className="bg-[var(--background-surface)] rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="font-medium text-[var(--text-primary)]">{factor.name}</span>
                  <span 
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      factor.status === 'Good' ? 'bg-[var(--status-success-subtle)] text-[var(--status-success)]' : 
                      factor.status === 'Elevated' ? 'bg-[var(--status-warning-subtle)] text-[var(--status-warning)]' : 
                      'bg-[var(--status-error-subtle)] text-[var(--status-error)]'
                    }`}
                  >
                    {factor.status}
                  </span>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mb-1">{factor.value}</p>
                <p className="text-xs text-[var(--text-tertiary)]">{factor.recommendation}</p>
              </div>
            ))}
          </div>
        </HealthCard>

        {/* Health Insights Card */}
        <HealthCard 
          title="Health Insights" 
          icon={BarChart3}
        >
          <div className="space-y-4 py-2">
            {insights.map((insight, index) => (
              <div key={index} className="flex space-x-3">
                <div className="flex-shrink-0 mt-1">
                  {getInsightIcon(insight.type)}
                </div>
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)] mb-1">{insight.title}</p>
                  <p className="text-xs text-[var(--text-secondary)]">{insight.message}</p>
                </div>
              </div>
            ))}
          </div>
          <button className="w-full mt-2 text-center text-sm py-2 bg-[var(--background-surface)] hover:bg-[var(--background-interactive)] text-[var(--text-primary)] rounded-lg transition-colors">
            View All Insights
          </button>
        </HealthCard>
      </div>
    </div>
  );
};

export default InsightsSection; 